import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { api, mediaUrl } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";
import StaggerWords from "@/components/site/StaggerWords";

function brandFromProject(project) {
  if (project.client && project.client.trim()) return project.client.trim();
  const t = project.title || "Project";
  if (t.includes("—")) return t.split("—")[0].trim();
  return t.split(" ").slice(0, 2).join(" ");
}

function ProjectCard({ project, index }) {
  const brand = brandFromProject(project);
  const ref = useRef(null);

  // Scroll-tied transforms — card scales up as it approaches viewport center,
  // shrinks slightly + fades on exit. Creates a "focus" rhythm.
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const scale = useTransform(scrollYProgress, [0, 0.35, 0.65, 1], [0.92, 1, 1, 0.95]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.4, 1, 1, 0.55]);

  return (
    <motion.div
      ref={ref}
      data-testid={`project-card-${project.id}`}
      className="group relative"
      style={{ scale, opacity }}
    >
      <Link to={`/proyecto/${project.id}`} className="block">
        {/* Index + arrow */}
        <div className="flex items-center justify-between mb-3 font-manrope text-[10px] uppercase tracking-[0.3em] text-bone/50">
          <span>{String(index + 1).padStart(2, "0")} / Client</span>
          <ArrowUpRight className="h-3.5 w-3.5 text-bone/40 group-hover:text-bone group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-300" />
        </div>

        {/* Logo plate — clip-path reveal on enter */}
        <motion.div
          className={`relative aspect-[4/3] overflow-hidden border border-ink-border ${
            project.logo_full_bleed ? "bg-ink-surface" : "bg-bone"
          }`}
          initial={{ clipPath: "inset(0% 100% 0% 0%)" }}
          whileInView={{ clipPath: "inset(0% 0% 0% 0%)" }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.0, ease: [0.76, 0, 0.24, 1], delay: (index % 3) * 0.08 }}
        >
          {project.logo_url ? (
            <img
              src={mediaUrl(project.logo_url)}
              alt={`${brand} logo`}
              loading="lazy"
              data-testid={`logo-${project.id}`}
              className={`absolute inset-0 h-full w-full transition-transform duration-700 group-hover:scale-105 ${
                project.logo_full_bleed
                  ? "object-cover"
                  : "object-contain p-4 md:p-6"
              }`}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-oswald uppercase tracking-tight text-ink text-5xl md:text-6xl">
                {brand}
              </span>
            </div>
          )}
        </motion.div>

        {/* Project meta */}
        <div className="pt-5">
          <div className="flex items-baseline justify-between gap-4 mb-2">
            <span
              data-testid={`brand-mark-${project.id}`}
              className="font-oswald uppercase tracking-tight text-2xl md:text-3xl text-bone leading-none"
            >
              {brand}
            </span>
            <span className="font-manrope text-[10px] uppercase tracking-[0.3em] text-bone/50 shrink-0">
              {project.year || ""}
            </span>
          </div>
          <h3 className="font-manrope text-sm md:text-base text-bone/80 leading-snug">
            {project.title}
          </h3>
          {project.category ? (
            <p className="mt-2 font-manrope text-[11px] uppercase tracking-[0.25em] text-bone/45">
              {project.category}
            </p>
          ) : null}
        </div>

        {/* Bottom CTA line that grows on hover */}
        <div className="mt-5 flex items-center gap-3">
          <span className="font-manrope text-[10px] uppercase tracking-[0.3em] text-bone/70">
            View case
          </span>
          <span className="h-px bg-bone/30 flex-1 origin-left scale-x-50 group-hover:scale-x-100 transition-transform duration-500" />
        </div>
      </Link>
    </motion.div>
  );
}

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let mounted = true;
    api
      .get("/projects")
      .then(({ data }) => {
        if (mounted) setProjects(data);
      })
      .catch((e) => {
        if (mounted) setErr(e.message || "Error loading projects");
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section
      id="projects"
      data-testid="projects-section"
      className="bg-ink relative py-24 md:py-40 px-6 md:px-12 border-t border-ink-border"
    >
      <div aria-hidden className="absolute left-3 md:left-6 top-0 bottom-0 w-px bg-[#E04F2A]/25 z-0" />
      <div aria-hidden className="absolute left-[10px] md:left-[19px] top-24 md:top-40 -translate-x-1/2 h-2.5 w-2.5 rounded-full border border-[#E04F2A] bg-ink z-0" />
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex items-baseline gap-4 mb-12">
          <span className="font-manrope text-xs uppercase tracking-[0.3em] text-bone/60 inline-flex items-center gap-2">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#E04F2A]" />
            03 · Selected work
          </span>
          <span className="h-px flex-1 bg-bone/15" />
          <span className="font-manrope text-xs uppercase tracking-[0.3em] text-bone/60 hidden md:block">
            {projects.length} pieces
          </span>
        </div>

        <h2 className="font-oswald uppercase tracking-tighter leading-[0.9] text-bone text-6xl md:text-8xl lg:text-9xl mb-20">
          <StaggerWords text="Recent" />
          <br />
          <StaggerWords
            text="projects."
            italic
            italicClassName="font-cormorant italic font-normal tracking-normal"
            delay={0.15}
          />
        </h2>

        {loading && (
          <div className="font-manrope text-bone/50 text-sm">Loading projects…</div>
        )}
        {err && !loading && (
          <div className="font-manrope text-red-400 text-sm">Could not load.</div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14 lg:gap-x-10">
          {projects.map((p, i) => (
            <ProjectCard key={p.id} project={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
